import { Unkey } from '@unkey/api';
import * as MockUnkey from './mockUnkey';
import { storeApiKey, getUserApiKeys } from './supabase';

// Debug: Log environment variables
console.log('Unkey Configuration:', {
  apiId: process.env.REACT_APP_UNKEY_API_ID ? 'Set' : 'Not set',
  rootKey: process.env.REACT_APP_UNKEY_ROOT_KEY ? 'Set' : 'Not set',
  apiIdValue: process.env.REACT_APP_UNKEY_API_ID,
  rootKeyValue: process.env.REACT_APP_UNKEY_ROOT_KEY ? process.env.REACT_APP_UNKEY_ROOT_KEY.substring(0, 10) + '***' : 'Not set'
});

// Check if Unkey is properly configured
const isUnkeyConfigured = process.env.REACT_APP_UNKEY_API_ID && 
                         process.env.REACT_APP_UNKEY_ROOT_KEY &&
                         process.env.REACT_APP_UNKEY_API_ID !== 'api_xxxxxxxxxxxxxx' &&
                         process.env.REACT_APP_UNKEY_ROOT_KEY !== 'unkey_xxxxxxxxxxxxxx';

console.log('Using Unkey:', isUnkeyConfigured ? 'Real' : 'Mock');

// Initialize Unkey client (only if properly configured)
const unkey = isUnkeyConfigured ? new Unkey({
  token: process.env.REACT_APP_UNKEY_ROOT_KEY,
  baseUrl: 'https://api.unkey.dev/v1', // Use development API
}) : null;

// API ID from environment
const API_ID = process.env.REACT_APP_UNKEY_API_ID;

// API Gateway base URL
const API_GATEWAY_URL = process.env.REACT_APP_API_GATEWAY_URL;

/**
 * Generate a new API key for a user
 * @param {string} userId - The user ID from Clerk
 * @param {string} userName - The user's name
 * @param {string} userEmail - The user's email
 * @returns {Promise<Object>} - The generated API key details
 */
export const generateApiKey = async (userId, userName, userEmail) => {
  // Use mock service if Unkey is not properly configured
  if (!isUnkeyConfigured || !unkey) {
    console.log('Using mock Unkey service');
    return MockUnkey.generateMockApiKey(userId, userName, userEmail);
  }

  try {
    console.log('Generating API key via API Gateway:', {
      userId,
      userName,
      userEmail
    });

    // Use API Gateway endpoint
    const response = await fetch(`${API_GATEWAY_URL}/generate-api-key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-User-ID': userId
      },
      body: JSON.stringify({
        name: `${userName}'s API Key`,
        project_id: 'default',
        user_id: userId,
        user_email: userEmail
      }),
    });

    console.log('🔍 API Gateway response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Gateway error:', response.status, errorText);
      throw new Error(`API Gateway error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('✅ API Gateway response:', result);

    // Store the API key in Supabase
    try {
      await storeApiKey(result, userId, userName, userEmail);
      console.log('✅ API key stored in Supabase');
    } catch (supabaseError) {
      console.error('❌ Failed to store API key in Supabase:', supabaseError);
      
      // If it's a duplicate key error, we can still return the key
      if (supabaseError.message && supabaseError.message.includes('duplicate key')) {
        console.log('⚠️ Key already exists in database, continuing...');
      } else {
        // For other errors, we might want to handle differently
        console.error('❌ Non-duplicate error storing API key:', supabaseError);
      }
      // Continue even if Supabase storage fails
    }

    return {
      key: result.key,
      keyId: result.keyId,
      meta: result.meta,
    };
  } catch (error) {
    console.error('Error generating API key with Unkey, falling back to mock:', error);
    
    // Fallback to mock service on error
    console.log('Falling back to mock Unkey service');
    return MockUnkey.generateMockApiKey(userId, userName, userEmail);
  }
};

/**
 * List all API keys for a user
 * @param {string} userId - The user ID from Clerk
 * @returns {Promise<Array>} - Array of API keys
 */
export const listApiKeys = async (userId) => {
  try {
    // Get API keys from Supabase
    const apiKeys = await getUserApiKeys(userId);

    // Transform Supabase data structure to match component expectations
    const transformedKeys = apiKeys.map(key => ({
      id: key.key_id, // Use key_id as the ID
      key: key.key_prefix ? key.key_prefix + '••••••••••••••••••••' : 'arms_••••••••••••••••••••', // Show masked key
      keyId: key.key_id,
      createdAt: key.created_at || new Date().toISOString(),
      meta: key.metadata || {},
      remaining: key.remaining_requests || 0,
      total: key.total_requests || 0,
      usage: {
        remaining: key.remaining_requests || 0,
        total: key.total_requests || 0,
        lastUsed: key.last_used
      }
    }));

    return transformedKeys;
  } catch (error) {
    console.error('Error listing API keys from Supabase, falling back to mock:', error);
    const mockKeys = await MockUnkey.listMockApiKeys(userId);
    return mockKeys;
  }
};

/**
 * Revoke an API key
 * @param {string} keyId - The key ID to revoke
 * @param {string} userId - The user ID who owns the key
 * @returns {Promise<boolean>} - Success status
 */
export const revokeApiKey = async (keyId, userId) => {
  try {
    // Use API Gateway endpoint to revoke the key - send user_id as query parameter (same as main.py)
    const response = await fetch(`${API_GATEWAY_URL}/revoke-api-key/${keyId}?user_id=${encodeURIComponent(userId)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to revoke key: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ API key revoked successfully:', result);
    return true;
  } catch (error) {
    console.error('Error revoking API key:', error);
    throw error;
  }
};

/**
 * Verify an API key
 * @param {string} key - The API key to verify
 * @returns {Promise<Object>} - Verification result
 */
export const verifyApiKey = async (key) => {
  try {
    // Use API Gateway endpoint
    const response = await fetch(`${API_GATEWAY_URL}/verify-api-key`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        api_key: key
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Gateway verification error:', response.status, errorText);
      throw new Error(`API Gateway verification error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log('API Gateway verification response:', result);

    return {
      valid: result.valid,
      ownerId: result.ownerId,
      meta: result.meta,
    };
  } catch (error) {
    console.error('Error verifying API key:', error);
    throw new Error('Failed to verify API key');
  }
}; 